import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  ClearBulkPaymentList,
  GetBulkPaymentList,
} from '@app/finxone-web-frontend/app/lib/actions/bulk-payment.action';
import {
  SetCopResponseStateMatchStatusCode,
  SetCopResponseStateName,
} from '@app/finxone-web-frontend/app/lib/actions/cop-response.action';
import { AccountService } from '@app/finxone-web-frontend/app/lib/services/account-service/account-service.service';

import { FormSubmissionService } from '@app/finxone-web-frontend/app/lib/services/form-submission-service/form-submission-service.service';
import { AccountState } from '@app/finxone-web-frontend/app/lib/state/account.state';
import { BulkPaymentListState } from '@app/finxone-web-frontend/app/lib/state/bulk-payment.state';
import { FormActionState } from '@app/finxone-web-frontend/app/lib/state/form-submision.state';
import { ProjectSettingsState } from '@app/finxone-web-frontend/app/lib/state/project-settings.state';
import { ProfileState } from '@app/finxone-web-frontend/app/lib/state/user-profile.state';
import { formatNameAsUrl } from '@app/finxone-web-frontend/app/lib/utils/zone-url.utils';
import {
  BulkPaymentBeneficiaryCoPStatus,
  BulkPaymentStatus,
  FormActionTypeEnum,
} from '@finxone-platform/form-action';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  APP_ZONES,
  BulkPaymentResultList,
  CheckPayeeMatchStatus,
  Columns,
  ProviderClass,
  UiZoneWidgetAttributeConfig,
  ZoneActionOptions,
} from '@finxone-platform/shared/sys-config-types';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, catchError, map, of, takeUntil, tap } from 'rxjs';
import { ZonePageUrlProvider } from '../../../zones/zone-url-provider';
import { BaseComponent } from '../../widgets/base/base.component';

@Component({
  standalone: false,
  selector: 'finxone-web-frontend-desktop-bulk-payment-listing',
  templateUrl: './desktop-bulk-payment-listing.component.html',
  styleUrl: './desktop-bulk-payment-listing.component.scss',
})
export class DesktopBulkPaymentListingComponent extends BaseComponent implements OnInit, OnDestroy {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @Select(BulkPaymentListState.getBulkPaymentList)
  entityListData!: Observable<any>;
  entityData: BulkPaymentResultList[];
  public pageNumber = 1;
  public entityName: string;
  public isShowOption = false;
  public isShowMoreOption = false;
  public totalListCount = 10;
  public isClickMenuIcon = false;
  public menuPopupCss: {};
  public actionOptions: ZoneActionOptions[] = [
    { label: 'Remove From List', value: 'remove', isIdNotExist: '' },
  ];
  private duplicateEntityColor = '#FFF4E1';
  private errorEntityColor = ' #FFE1E1';
  public beneficiaryCheckEntityColor = '#CDE4FF';
  public masterFileId = '';
  public threeDotStyles: object;
  private searchText = '';
  public menuItemsStatus: {
    [key: string]: { [key: string]: boolean };
  } = {};
  private checkPayeeEnabled: boolean;
  private pageEntityIntervalRef: any;

  /**
   * This variable is used in the below function
   * {@link isPaymentRequestorRestrictedRoute}
   */
  private readonly paymentRequestorRestrictedRoutes = [
    `bulk-payment-summary-elaborated-view`,
    `bulk-payment-summary-elaborated-view-approver-reject`,
  ];

  constructor(
    private router: Router,
    public store: Store,
    public dataIn: ProviderClass<UiZoneWidgetAttributeConfig>,
    private alertHandlerService: AlertHandlerService,
    private accountService: AccountService,
    private zonePageUrlProvider: ZonePageUrlProvider,
    public formSubmissionService: FormSubmissionService,
  ) {
    super();
  }

  ngOnInit() {
    if (
      this.dataIn?.attri?.widgetProperties?.['menuOptions'] ||
      this.dataIn?.attri?.widgetProperties?.['memuOptions']
    ) {
      this.actionOptions =
        this.dataIn?.attri?.widgetProperties?.['menuOptions'] ||
        this.dataIn?.attri?.widgetProperties?.['memuOptions'];
    }
    this.threeDotStyles = { right: '3%' };

    this.onModificationForCheckPayee();

    this.isShowMoreOption = true;

    this.duplicateEntityColor = this.dataIn.attri['duplicateRowColor'] ?? this.duplicateEntityColor;
    this.errorEntityColor = this.dataIn.attri['errorRowColor'] ?? this.errorEntityColor;
    this.beneficiaryCheckEntityColor =
      this.dataIn.attri['beneficiaryCheckColor'] ?? this.beneficiaryCheckEntityColor;

    const formActionData = this.store.selectSnapshot(
      FormActionState.getFormActionStateWithId(FormActionTypeEnum.BULK_PAYMENTS),
    );
    this.masterFileId = formActionData?.formData.masterFileId;

    this.startPollingPageEntity();
  }

  public startPollingPageEntity() {
    this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId));
    this.getPageEntity(); // Initial call to load data

    this.pageEntityIntervalRef = setInterval(() => {
      this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId)); // Repeated call to load data
    }, 5000); // Poll every 5 seconds
  }

  ngOnDestroy() {
    if (this.pageEntityIntervalRef) {
      clearInterval(this.pageEntityIntervalRef);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onModificationForCheckPayee() {
    this.checkPayeeEnabled = this.store.selectSnapshot(
      ProjectSettingsState.getProjectSettings,
    )?.checkPayeeEnabled;

    /**
     * @description: if checkPayee is enabled then we have to show beneficiaryExistAndCopStatus or else default beneficiaryExistStatus
     */

    if (
      this.checkPayeeEnabled &&
      this.dataIn?.widgetConfig?.attributes?.['columns'] &&
      this.dataIn?.attri?.['columns']
    ) {
      const columns = (this.dataIn.widgetConfig.attributes['columns'] as Columns[]).map((column) => {
        if (column?.label === 'New / Existing') {
          return {
            ...column,
            value: 'removeUnderscoreAndCapitalise beneficiaryExistAndCopStatus',
            handleBarKey: '{{removeUnderscoreAndCapitalise beneficiaryExistAndCopStatus}}',
          };
        }
        return column;
      });

      this.dataIn.widgetConfig.attributes['columns'] = columns;
      this.dataIn.attri['columns'] = columns;
    }
  }

  public get pageSize(): number {
    if (this.dataIn.attri['pageSize'] != null) return Number(this.dataIn.attri['pageSize']);
    else return 10;
  }

  public get pagingEnabled(): boolean {
    if (this.dataIn.attri['pagingEnabled'] != null) return this.dataIn.attri['pagingEnabled'];
    else return true;
  }

  handleScroll() {
    if (this.pagingEnabled) {
      if (this.pageNumber * this.pageSize === this.entityData.length) {
        this.pageNumber = this.pageNumber + 1;
        this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId));
      }
    }
  }
  @HostListener('scroll', ['$event'])
  onScroll(event: Event) {
    const el = event.target as HTMLElement;
    const scrollPerc = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
    if (scrollPerc > 80) {
      this.handleScroll();
    }
  }

  public getPageEntity() {
    const profile = this.store.selectSnapshot(ProfileState.getProfile);
    const systemConfig = this.store.selectSnapshot(AccountState.getPaymentGatewayConfig);
    const activeOrg = profile.orgList?.find((org) => org.id === profile.activeOrganisationId);

    this.entityListData
      .pipe(
        takeUntil(this.destroy$),
        map((listEntity) => {
          this.menuItemsStatus = {};
          this.entityData = [];
          this.totalListCount = listEntity.totalItems;

          const isPaymentRequestor =
            systemConfig?.paymentRequest?.enabled &&
            !!activeOrg?.roles.find((role) =>
              systemConfig?.paymentRequest?.paymentRequestorRoles.includes(role),
            );

          this.entityData = listEntity?.result?.map((response: BulkPaymentResultList, index: number) => {
            const rowNumber = this.pageSize * (this.pageNumber - 1) + (index + 1);
            response['row'] = rowNumber < 10 ? '0' + rowNumber : (rowNumber as unknown as string);

            // Set entity style based on status
            if (response.status === BulkPaymentStatus.DUPLICATE_RECORDS) {
              response['entityStyle'] = { 'background-color': this.duplicateEntityColor };
            } else if (
              response?.beneficiaryExistAndCopStatus !== CheckPayeeMatchStatus.MATCHED &&
              (response?.beneficiaryCoPStatus === BulkPaymentStatus.PENDING ||
                response?.beneficiaryCoPStatus === BulkPaymentBeneficiaryCoPStatus.APPROVED) &&
              this.checkPayeeEnabled
            ) {
              response['entityStyle'] = { 'background-color': this.beneficiaryCheckEntityColor };
            } else if (response.status === BulkPaymentStatus.FAILED) {
              response['entityStyle'] = { 'background-color': this.errorEntityColor };
            } else {
              response['entityStyle'] = '';
            }

            // hide the inspect suggestion option for otherthan CLOSE_MATCH
            if (response?.beneficiaryExistAndCopStatus?.toLowerCase() !== CheckPayeeMatchStatus.CLOSE_MATCH) {
              this.menuItemsStatus = {
                ...this.menuItemsStatus,
                [response.id]: {
                  ...this.menuItemsStatus?.[response.id],
                  'inspect-suggestion': false,
                },
              };
            }

            // hide the inspect suggestion and continue anyway options when status is not failed
            if (response?.status !== BulkPaymentStatus.FAILED) {
              this.menuItemsStatus = {
                ...this.menuItemsStatus,
                [response.id]: {
                  ...this.menuItemsStatus?.[response.id],
                  'inspect-suggestion': false,
                  'continue-anyway': false,
                },
              };
            }

            /**
             *  Disable action if user is a payment requestor and routes are restricted
             * {@link paymentRequestorRestrictedRoutes}
             */
            if (isPaymentRequestor && this.isPaymentRequestorRestrictedRoute) {
              response['actionEnabled'] = false;
            }

            return response;
          });

          let pendingKey = 0;
          const currentPageUrl = this.zonePageUrlProvider.pageUrlInput;
          const bulkPaymentsSummary = listEntity?.meta?.bulkPaymentsSummary;

          if (bulkPaymentsSummary) {
            switch (currentPageUrl) {
              case 'bulk-payment-beneficiary-review':
                pendingKey = bulkPaymentsSummary.totalPendingPayments ?? 0;
                break;
              case 'bulk-payment-review-error-handling':
                pendingKey = bulkPaymentsSummary.totalPendingBeneficiary ?? 0;
                break;
              case 'bulk-payment-summary-elaborated-view':
                if (bulkPaymentsSummary.masterStatus === 'authenticated') {
                  const pendingBeneficiary = bulkPaymentsSummary.totalPendingBeneficiary ?? 0;
                  pendingKey = pendingBeneficiary > 0 ? 0 : bulkPaymentsSummary.totalPendingTransactions ?? 0;
                }
                break;
            }
          }

          if (pendingKey === 0) {
            clearInterval(this.pageEntityIntervalRef); // Stop polling if no more pending items
          }
        }),
      )
      .subscribe();
  }

  onPageChange(event: { first: number; page: number; pageCount: number; rows: number }) {
    this.pageNumber = event.first / event.rows + 1;
    this.dataIn.attri['pageSize'] = event.rows;
    this.store.dispatch(
      new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId, this.searchText),
    );
  }

  public performAction(event: { actionKey: string; actionId: string }) {
    switch (event.actionKey) {
      case 'retry':
        this.retryTransaction(event.actionId);
        break;
      case 'remove':
        this.removeTransaction(event.actionId);
        break;

      case 'inspect-suggestion':
        this.onClickInspectSuggestion(event.actionId);
        break;

      case 'continue-anyway':
        this.onClickContinueAnyway(event.actionId);
        break;

      case 'skip':
        this.onClickSkip(event.actionId);
        break;

      default:
        break;
    }
  }

  private onClickInspectSuggestion(id: string) {
    const beneficiaryItem = this.entityData.find((item) => item.id === id);
    if (beneficiaryItem) {
      const beneficiaryBankAccount = beneficiaryItem?.beneficiaryBankAccounts?.[0];

      // setting up beneficiary details into form state
      this.formSubmissionService.submitIndividualForm(
        {
          name: beneficiaryItem.name,
          accountType: beneficiaryItem.type,
          accountId: beneficiaryItem.id,
          destinationIdentifier: {
            accountNumber: beneficiaryBankAccount?.account_number,
            sortCode: beneficiaryBankAccount?.sort_code,
            country: beneficiaryBankAccount?.country,
            currency: beneficiaryItem.currency,
          },
        },
        '',
        FormActionTypeEnum.ADD_BENEFICIARY,
      );

      // setting up transaction Id into form state
      this.formSubmissionService.submitIndividualForm(
        {
          transactionId: id,
        },
        '',
        FormActionTypeEnum.BULK_PAYMENTS,
      );

      // dispatch the COP details into state
      this.store.dispatch(new SetCopResponseStateName(beneficiaryItem?.beneficiaryCoPName as string));
      this.store.dispatch(
        new SetCopResponseStateMatchStatusCode(beneficiaryItem?.beneficiaryCoPCode as CheckPayeeMatchStatus),
      );

      this.router.navigateByUrl(
        `/zones/${formatNameAsUrl(APP_ZONES.PAYMENT)}/review-information-close-match-cop?flow=bulk-payment`,
      );
    }
  }

  private onClickContinueAnyway(id: string) {
    this.accountService
      .bulkPaymentContinueAnywayAndWithSuggestion(this.masterFileId, id, { suggestion: false })
      .pipe(
        map((response) => {
          if (response) {
            this.store.dispatch(new ClearBulkPaymentList());
            this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId));
            this.alertHandlerService.showAlertFn('success', 'Updated Successfully.');
          }
        }),
        catchError((error) => {
          console.error('Error While calling bulkPaymentContinueAnywayAndWithSuggestion:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private onClickSkip(id: string) {
    this.removeTransaction(id);
  }

  public retryTransaction(id: string) {
    this.accountService
      .retryBulkPaymentTransfer(this.masterFileId, id)
      .pipe(
        map((response) => {
          if (response) {
            this.pageNumber = 1;
            this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId));
            this.alertHandlerService.showAlertFn('success', 'Transaction Successfull');
          }
        }),
        catchError((error) => {
          console.error('Error While Retry transaction:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  public removeTransaction(id: string) {
    this.accountService
      .deleteTransaction(this.masterFileId, id)
      .pipe(
        tap(() => {
          this.store.dispatch(new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId));
          this.alertHandlerService.showAlertFn('success', 'Updated Successfully.');
        }),
      )
      .subscribe();
  }
  public searchValue(searchText: string) {
    this.searchText = searchText;
    this.pageNumber = 1;
    this.store.dispatch(
      new GetBulkPaymentList(this.pageNumber, this.pageSize, this.masterFileId, searchText),
    );
  }

  /**
   * Check the valid route for the payment requestor.
   * As a payment requestor, The menu options wont be displayed
   * in the below route for the table list.
   * @function isPaymentRequestorRestrictedRoute
   * {@link paymentRequestorRestrictedRoutes} restricted routes
   * {@link location.pathname} used this for getting current route
   */
  get isPaymentRequestorRestrictedRoute() {
    return this.paymentRequestorRestrictedRoutes.some((restrictedRoute) =>
      location.pathname?.includes(restrictedRoute),
    );
  }
}
